import React from "react";
import { COLORS } from "../constants";
import Select, { components } from "react-select";
import { Modal, ModalBody, Button } from "reactstrap";
import { getCountryFlag } from "../utils";

// -------- Flag Component ----------
export const CountryFlag = ({ countryName, style = {} }) => {
  const actualCountry = countryName.includes("(")
    ? countryName.split("(")[0].trim()
    : countryName;

  const FlagComponent = getCountryFlag(actualCountry);
  if (!FlagComponent) return null;

  return (
    <FlagComponent
      style={{
        width: "20px",
        height: "auto",
        marginRight: "6px",
        verticalAlign: "middle",
        ...style,
      }}
    />
  );
};

// -------- React-Select Option ----------
const CountryOption = (props) => {
  const { innerRef, innerProps, data, isFocused, isSelected } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 12px",
        background: isFocused ? "#3A3B47" : "transparent",
        color: isSelected ? COLORS.primary : COLORS.white,
        cursor: "pointer",
      }}
    >
      <CountryFlag countryName={data.label} />
      {data.label}
    </div>
  );
};

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <CountryFlag countryName={props.data.label} />
    {props.data.label}
  </components.SingleValue>
);

// -------- MAIN COMPONENT ----------
const FilterControls = ({
  countriesData = [],
  selectedCountry,
  selectedLanguage,
  availableLanguages = [],
  selectionError,
  onCountrySelect,
  onCountryChange,
  onLanguageChange,

  trackedCountries = [],
  trackedCountryLanguagePairs = [],
  activeTrackedCountry = "",
  activeTrackedPair = "",
  onTrackedCountryClick,
  onTrackedPairClick,

  onRemoveTrackedPair = () => { },

  hasCategoriesForPair = null,
  trackedApp = null,
}) => {

  // ---------- Local buffered selection ----------
  const [localCountry, setLocalCountry] = React.useState(selectedCountry || "");
  const [localLanguage, setLocalLanguage] = React.useState(
    selectedLanguage || ""
  );

  const [pairToRemove, setPairToRemove] = React.useState(null);

  React.useEffect(() => {
    setLocalCountry(selectedCountry || "");
  }, [selectedCountry]);

  React.useEffect(() => {
    setLocalLanguage(selectedLanguage || "");
  }, [selectedLanguage]);

  return (
    <div className="track_data_sec">
      {/* -------- Country + Language Selectors -------- */}
      <div
        className="track_country_main"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "8px",
          marginTop: "12px",
        }}
      >
        <label style={{ color: COLORS.white }}>
          Select Country & Language
          {!selectedCountry || !selectedLanguage ? (
            <span style={{
              color: "#94a3b8",
              marginLeft: "8px",
              fontSize: "12px",
              fontWeight: "normal"
            }}>
              Required to add genres
            </span>
          ) : (
            <span style={{
              color: "#4ade80",
              marginLeft: "8px",
              fontSize: "12px",
              fontWeight: "normal"
            }}>
              Ready to add genres
            </span>
          )}
        </label>

        <div
          className="combined-select"
          style={{
            display: "flex",
            alignItems: "center",
            background: !selectedCountry || !selectedLanguage ? "#2A2B37" : "#1a1b23",
            border: !selectedCountry || !selectedLanguage ? "2px solid #f43f5e" : "1px solid #3A3B47",
            borderRadius: "6px",
            padding: "6px 8px",
            gap: "10px",
            transition: "all 0.3s ease",
            position: "relative"
          }}
        >
          <div style={{ position: 'relative', minWidth: 160 }}>
            <Select
              options={countriesData.map((c) => ({ value: c.name, label: c.name }))}
              value={
                localCountry ? { value: localCountry, label: localCountry } : null
              }
              onChange={(option) => {
                const val = option?.value || "";
                setLocalCountry(val);
                // Call both handlers to ensure state is updated
                if (typeof onCountrySelect === 'function') {
                  onCountrySelect(val);
                }
                if (typeof onCountryChange === 'function') {
                  onCountryChange?.({ target: { value: val } });
                }
              }}
              placeholder="Select Country"
              components={{ Option: CountryOption, SingleValue }}
              styles={{
                control: (p) => ({
                  ...p,
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  minHeight: "36px",
                }),
                singleValue: (p) => ({
                  ...p,
                  color: COLORS.white,
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }),
                menu: (p) => ({ ...p, background: "#2A2B37", color: COLORS.white }),
                option: (p, s) => ({
                  ...p,
                  background: s.isFocused ? "#3A3B47" : "transparent",
                  color: s.isSelected ? COLORS.primary : COLORS.white,
                }),
                dropdownIndicator: (p) => ({ ...p, color: "#aaa" }),
                indicatorSeparator: (p) => ({ ...p, backgroundColor: "#3A3B47" }),
              }}
            />
          </div>

          <div
            style={{
              width: "1px",
              height: "28px",
              background: "#3A3B47",
              margin: "0 10px",
            }}
          />

          <div style={{ position: 'relative', minWidth: 160 }}>
            <Select
              options={availableLanguages.map((l) => ({ value: l, label: l }))}
              value={
                localLanguage
                  ? { value: localLanguage, label: localLanguage }
                  : null
              }
              onChange={(option) => {
                const val = option?.value || "";
                setLocalLanguage(val);
                if (typeof onLanguageChange === 'function') {
                  onLanguageChange({ target: { value: val } });
                }
              }}
              placeholder="Select Language"
              styles={{
                control: (p) => ({
                  ...p,
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  minHeight: "36px",
                }),
                singleValue: (p) => ({
                  ...p,
                  color: COLORS.white,
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }), 
                menu: (p) => ({ ...p, background: "#2A2B37", color: COLORS.white }),
                option: (p, s) => ({
                  ...p,
                  background: s.isFocused ? "#3A3B47" : "transparent",
                  color: s.isSelected ? COLORS.primary : COLORS.white,
                }),
                dropdownIndicator: (p) => ({ ...p, color: "#aaa" }),
                indicatorSeparator: (p) => ({ ...p, backgroundColor: "#3A3B47" }),
              }}
            />
          </div>
        </div>
      </div>

      {/* -------- Remove Confirmation Modal -------- */}
      <Modal
        isOpen={!!pairToRemove}
        toggle={() => setPairToRemove(null)}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">Confirm Removal</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setPairToRemove(null)}
            aria-label="Close"
          />
        </div>

        <ModalBody style={{ textAlign: "center", padding: "20px" }}>
          {hasCategoriesForPair && hasCategoriesForPair(pairToRemove) ? (
            <p>
              The pair "<b>{pairToRemove}</b>" has tracked categories. Removing it
              will also delete those category records. <br />
              This action cannot be undone. Do you want to proceed?
            </p>
          ) : (
            <p>
              Are you sure you want to remove "<b>{pairToRemove}</b>"? Tracked
              category data will be removed.
            </p>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginTop: "12px",
            }}
          >
            <Button
              color="danger"
              onClick={() => {
                if (pairToRemove) {
                  onRemoveTrackedPair(pairToRemove, {
                    deleteCategories: true,
                  });
                }
                setPairToRemove(null);
              }}
            >
              Yes
            </Button>

            <Button color="secondary" onClick={() => setPairToRemove(null)}>
              Cancel
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Error */}
      {selectionError && (
        <div style={{ color: COLORS.error, marginTop: "8px" }}>
          Please select both Country and Language before selecting a category.
        </div>
      )}
    </div>
  );
};

export default FilterControls;
